import { useState, useEffect } from 'react';
import { useNavigate, useLocation, Link } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';

export function ResetPassword() {
  const [email, setEmail] = useState('');
  const [otp, setOtp] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [error, setError] = useState('');
  const [message, setMessage] = useState('');
  const [step, setStep] = useState<'otp' | 'password'>('otp');
  const { verifyOtpAndResetPassword, loading } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();

  useEffect(() => {
    // Get email from navigation state (from forgot password page)
    const stateEmail = location.state?.email;
    if (stateEmail) {
      setEmail(stateEmail);
    } else {
      // If no email in state, user probably accessed this page directly
      setError('Please start the password reset process from the forgot password page.');
    }
  }, [location.state]);

  const handleOtpSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setMessage('');

    if (!email || !otp) {
      setError('Please enter the OTP code');
      return;
    }

    if (otp.length !== 6) {
      setError('OTP must be 6 digits');
      return;
    }

    // Move to password step - we'll verify OTP when user submits password
    setStep('password');
    setMessage('OTP accepted! Now set your new password.');
  };

  const handlePasswordSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setMessage('');

    // Validate password match
    if (password !== confirmPassword) {
      setError('Passwords do not match');
      return;
    }

    // Validate password length
    if (password.length < 6) {
      setError('Password must be at least 6 characters long');
      return;
    }

    try {
      const { error: resetError } = await verifyOtpAndResetPassword(email, otp, password);
      if (resetError) {
        // If OTP is invalid, go back to OTP step
        if (resetError.message?.includes('invalid') || resetError.message?.includes('expired')) {
          setStep('otp');
          setError('Invalid or expired OTP. Please enter the correct code.');
          return;
        }
        throw resetError;
      }
      setMessage('Password updated successfully! Redirecting to dashboard...');
      
      // Redirect to dashboard after successful password update
      setTimeout(() => {
        navigate('/dashboard');
      }, 2000);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to reset password');
    }
  };

  const handleBackToOtp = () => {
    setStep('otp');
    setError('');
    setMessage('');
    setPassword('');
    setConfirmPassword('');
  };

  // Show error if no email provided
  if (!email && !loading) {
    return (
      <div className="pen-screen flex items-center justify-center px-5 py-8">
        <div className="pen-card w-full max-w-md border-t-[3px] border-t-sw-yellow text-center">
          <h1 className="pen-title">Invalid Access</h1>
          <p className="pen-lede">
            Please start the password reset process from the forgot password page.
          </p>
          <Link to="/forgot-password" className="pen-link">
            Go to Forgot Password
          </Link>
        </div>
      </div>
    );
  }

  if (step === 'otp') {
    return (
      <div className="pen-screen flex items-center justify-center px-5 py-8">
        <div className="pen-card w-full max-w-md border-t-[3px] border-t-sw-yellow">
          <h1 className="pen-title">Enter OTP Code</h1>
          <p className="pen-lede">
            We&apos;ve sent a 6-digit code to <strong className="text-white">{email}</strong>
          </p>

          {error && <div className="pen-error mb-4">{error}</div>}
          {message && <div className="pen-success mb-4">{message}</div>}

          <form className="space-y-3" onSubmit={handleOtpSubmit}>
            <div>
              <label htmlFor="otp" className="pen-label">
                OTP code
              </label>
              <input
                id="otp"
                name="otp"
                type="text"
                inputMode="numeric"
                pattern="[0-9]*"
                maxLength={6}
                required
                className="pen-input font-mono text-lg text-center tracking-[0.3em]"
                placeholder="000000"
                value={otp}
                onChange={(e) => setOtp(e.target.value.replace(/\D/g, '').slice(0, 6))}
              />
            </div>

            <button
              type="submit"
              disabled={loading || otp.length !== 6}
              className="pen-btn-primary w-full"
            >
              {loading ? 'Verifying...' : 'Verify OTP'}
            </button>
          </form>

          <Link to="/forgot-password" className="pen-link block mt-4">
            Didn&apos;t receive the code? Send again
          </Link>
        </div>
      </div>
    );
  }

  // Password step
  return (
    <div className="pen-screen flex items-center justify-center px-5 py-8">
      <div className="pen-card w-full max-w-md border-t-[3px] border-t-sw-yellow">
        <h1 className="pen-title">Set New Password</h1>
        <p className="pen-lede">
          Enter your new password for <strong className="text-white">{email}</strong>
        </p>

        {error && <div className="pen-error mb-4">{error}</div>}
        {message && <div className="pen-success mb-4">{message}</div>}

        <form className="space-y-3" onSubmit={handlePasswordSubmit}>
          <div>
            <label htmlFor="password" className="pen-label">
              New password
            </label>
            <input
              id="password"
              name="password"
              type="password"
              autoComplete="new-password"
              required
              className="pen-input"
              placeholder="New password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              minLength={6}
            />
          </div>
          <div>
            <label htmlFor="confirm-password" className="pen-label">
              Confirm new password
            </label>
            <input
              id="confirm-password"
              name="confirm-password"
              type="password"
              autoComplete="new-password"
              required
              className="pen-input"
              placeholder="Confirm new password"
              value={confirmPassword}
              onChange={(e) => setConfirmPassword(e.target.value)}
              minLength={6}
            />
          </div>

          <button
            type="submit"
            disabled={loading || password !== confirmPassword || password.length < 6}
            className="pen-btn-primary w-full"
          >
            {loading ? 'Updating...' : 'Update Password'}
          </button>
        </form>

        <button onClick={handleBackToOtp} className="pen-link block w-full mt-4">
          Back to OTP entry
        </button>
      </div>
    </div>
  );
}
